import { forwardRef, ReactElement, Ref } from 'react'
import { useDispatch } from 'react-redux'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Slide, Tab, Tabs, Typography } from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import { CloseRounded } from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import { CancelButton, SaveButton } from '../../../../../shared/components/inputs'
import { toggleFormOffer } from '../../../../../../redux/actions/offers'
import 'react-dropzone-uploader/dist/styles.css'
import { useTranslation } from 'react-i18next'
import TabPanel from '../../../../../shared/components/TabPanel'
import useScreenWidth from '../../../../../shared/hooks/useScreenWidth'
import OffersModalGeneralInfo from './OffersModalGeneralInfo'
import OffersModalGeneralTab from './OffersModalGeneralTab'
import OffersModalAddonsTab from './OffersModalAddonsTab'
import OffersModalDownloadableTab from './OffersModalDownloadableTab'
import { formsStyles } from '../../../../../shared/styles/FormsStyles'
import { SxProps, Theme } from '@mui/system'
import { useOffersModal } from '../../hooks/useOffersModal'

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  }
}

const Transition = forwardRef(function Transition(props: TransitionProps & { children: ReactElement }, ref: Ref<unknown>) {
  return <Slide direction="up" ref={ref} {...props} />
})

const OffersModal = () => {
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)
  const dispatch = useDispatch()
  const theme = useTheme()
  const formClasses = formsStyles(theme)

  const { showForm, offerState, setOfferState, currentTab, errorForm, errorTab, msgForm, isValidForm, handleChangeTab, handleSubmit } =
    useOffersModal()

  return (
    <Dialog
      open={showForm}
      fullWidth
      fullScreen={isMobile}
      maxWidth={isMobile ? false : 'md'}
      TransitionComponent={Transition}
      onClose={() => dispatch(toggleFormOffer())}
      sx={formClasses.containerForm as SxProps<Theme>}
      aria-describedby="alert-dialog-description"
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('create-offer')}
          </Typography>
          <Button onClick={() => dispatch(toggleFormOffer())} color="primary" variant="text">
            <CloseRounded />
          </Button>
        </Grid>
      </DialogTitle>

      <DialogContent sx={formClasses.bodyForm as SxProps<Theme>}>
        <OffersModalGeneralInfo offerState={offerState} setOfferState={setOfferState} errorForm={errorForm} msgForm={msgForm} />

        <Grid item xs={12}>
          <Tabs
            value={currentTab}
            onChange={handleChangeTab}
            indicatorColor="secondary"
            textColor="primary"
            variant="scrollable"
            sx={formClasses.tapForm as SxProps<Theme>}
            aria-label="disabled tabs example"
          >
            <Tab label={t('general')} {...a11yProps(0)} sx={errorTab.general ? (formClasses.errorTab as SxProps<Theme>) : undefined} />
            <Tab label={t('add-ons')} {...a11yProps(1)} sx={errorTab.addOn ? (formClasses.errorTab as SxProps<Theme>) : undefined} />
            <Tab
              label={t('downloadable-material')}
              {...a11yProps(2)}
              sx={errorTab.general ? (formClasses.errorTab as SxProps<Theme>) : undefined}
            />
          </Tabs>
        </Grid>

        <TabPanel value={currentTab} index={0}>
          <OffersModalGeneralTab msgForm={msgForm} setOfferState={setOfferState} errorForm={errorForm} offerState={offerState} />
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <OffersModalAddonsTab offerState={offerState} setOfferState={setOfferState} />
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <OffersModalDownloadableTab setOfferState={setOfferState} offerState={offerState} />
        </TabPanel>
      </DialogContent>

      <DialogActions sx={!isValidForm && Object.keys(msgForm).length > 0 ? (formClasses.errorDialogActions as SxProps<Theme>) : undefined}>
        <CancelButton onClick={() => dispatch(toggleFormOffer())}>{t('cancel')}</CancelButton>
        <SaveButton onClick={handleSubmit}>{t('save')}</SaveButton>
      </DialogActions>
    </Dialog>
  )
}

export default OffersModal
